import { FC } from 'react';

import { Box, Grid, Typography } from '@mui/material';
import { FormikProps, useField } from 'formik';
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from 'react-icons/ai';

import { FieldInput } from 'src/components';
import { CustomRadioGroup } from 'src/components/FormikComponents';
import { PercentageInput } from 'src/components/FormikComponents/PercentageInput';
import { useAddMerchantContext } from 'src/contexts/AddMerchantProvider';
import { getCountryName } from 'src/lib/countryConfig';
import { isApplicationEditable } from 'src/pages/MerchantApplicationPage/merchantApplicationHelpers.util';
import { Principal } from 'src/types/AddMerchant/merchantInfo';

import { principalInitialValue } from '../..';
import AddressesFieldArray from '../../../AddressArray';
import PrincipalDetails from '../PrincipalDetails';

interface PrincipalItem {
    principalIndex: number;
    push: (principal: object) => void;
    formik: FormikProps<any>;
    remove: (index: number) => void;
    principal: Principal;
    principals: Principal[];
}

const PrincipalItem: FC<PrincipalItem> = ({
    principalIndex,
    push,
    formik,
    remove,
    principal,
    principals,
}) => {
    const name = `principals.${principalIndex}`;
    const [{ value: isBeneficialOwner }, , { setValue: setIsBeneficialOwner }] = useField<boolean>(
        `${name}.isBeneficialOwner`
    );
    const [{ value: _ }, , { setValue: setIsBillingContact }] = useField<boolean>(
        `${name}.isBillingContact`
    );
    const { merchantApplication } = useAddMerchantContext();
    const isEditingDisabled = isApplicationEditable(merchantApplication);

    return (
        <>
            <Grid container gap="24px" direction="column">
                <PrincipalDetails
                    name={name}
                    principal={principal}
                    formik={formik}
                    isEditingDisabled={isEditingDisabled}
                />
                <Box display="flex" gap="24px">
                    {principals.length > 1 && (
                        <CustomRadioGroup
                            name={`${name}.isBillingContact`}
                            label="Is Billing Contact"
                            options={[
                                { label: 'Yes', value: true },
                                { label: 'No', value: false },
                            ]}
                            onChange={(e, value) => {
                                if (value === 'true') {
                                    principals.forEach((_, idx) => {
                                        if (idx !== principalIndex) {
                                            formik.setFieldValue(
                                                `principals.${idx}.isBillingContact`,
                                                false
                                            );
                                        }
                                    });
                                }
                                setIsBillingContact(value === 'true');
                            }}
                        />
                    )}
                    <CustomRadioGroup
                        name={`${name}.isBeneficialOwner`}
                        label="Beneficial Owner"
                        options={[
                            { label: 'Yes', value: true },
                            { label: 'No', value: false },
                        ]}
                        onChange={(_e, value) => {
                            setIsBeneficialOwner(value === 'true');
                        }}
                    />
                    {isBeneficialOwner && (
                        <PercentageInput
                            name={`${name}.percentageOfOwnership`}
                            label="Percentage of ownership"
                            value={principal.percentageOfOwnership}
                            disabled={isEditingDisabled}
                        />
                    )}
                </Box>
                <FieldInput
                    name={`${name}.position`}
                    label="Position"
                    disabled={isEditingDisabled}
                />
                <AddressesFieldArray
                    principal={principal}
                    principals={principals}
                    formik={formik}
                    principalIndex={principalIndex}
                />
            </Grid>
            <Box display="flex" alignItems="center" gap="16px">
                {formik.values.principals.length - 1 === principalIndex && (
                    <Box
                        display="flex"
                        alignItems="center"
                        marginTop="24px"
                        onClick={() => {
                            if (
                                !merchantApplication?.status ||
                                merchantApplication?.status === 'DRAFT'
                            ) {
                                push({
                                    ...principalInitialValue,
                                    addresses: [
                                        {
                                            ...principalInitialValue.addresses[0],
                                            country: getCountryName(
                                                merchantApplication?.partner?.countryDetails
                                                    ?.alpha2Code
                                            ),
                                        },
                                    ],
                                });
                            }
                        }}
                        sx={{ cursor: 'pointer' }}
                    >
                        <AiOutlinePlusCircle color="#F3A256" size="24px" />
                        <Typography
                            fontSize="16px"
                            color="#575757"
                            lineHeight="24px"
                            fontWeight="400"
                            marginLeft="8px"
                        >
                            Add Principal
                        </Typography>
                    </Box>
                )}
                {formik.values.principals.length > 1 && (
                    <Box
                        display="flex"
                        alignItems="center"
                        marginTop="24px"
                        onClick={() => {
                            if (
                                !merchantApplication?.status ||
                                merchantApplication?.status === 'DRAFT'
                            ) {
                                remove(principalIndex);
                            }
                        }}
                        sx={{ cursor: 'pointer' }}
                    >
                        <AiOutlineMinusCircle color="#f36e56" size="24px" />
                        <Typography
                            fontSize="16px"
                            color="#575757"
                            lineHeight="24px"
                            fontWeight="400"
                            marginLeft="8px"
                        >
                            {`Remove Principal ${principalIndex + 1}`}
                        </Typography>
                    </Box>
                )}
            </Box>
        </>
    );
};

export default PrincipalItem;
